<template>
	<header :class="[classes, { bb: setBorder }]" data-context="header">
		<div class="container">
			<div class="page__header-inner header">
				<div class="header-left">
					<slot name="back">
						<BaseButton
							v-if="backLink"
							data-test="header-back-link"
							classes="display-flex align-items-center"
							:mixpanel-custom-properties="mixpanelCustomProperties"
							@click="goToPreviousStep()"
						>
							<BaseSvgIcon name="chevron" dir="left" width="24" height="24" /><span
								:class="appStore.isMobile ? 'body-sm' : 'body-md'"
								>Back</span
							>
						</BaseButton>
					</slot>
				</div>

				<div class="header-logo">
					<BaseLink v-if="enableHomeLink" :link="{ router: logoRoute }">
						<img :src="logoImgUrl" :class="logoClasses" alt="Fundrise logo" />
					</BaseLink>
					<img v-else :src="logoImgUrl" :class="logoClasses" alt="Fundrise logo" />
				</div>

				<div class="header-right">
					<BaseButton
						v-if="cancelLink"
						data-test="cancel-link-mobile"
						classes="text-color-current-color link"
						:click-text="cancelClickText"
						@click="exitFlow()"
					>
						<span class="show-for-sr">Cancel</span>
						<BaseSvgIcon name="close-light" width="24" height="24" />
					</BaseButton>
				</div>
			</div>
		</div>
	</header>
</template>
<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';
import { AccountInvestFlowType } from 'types/account/invest';
import { appCloudfrontPath } from '@constants';
import { computed } from 'vue';
import { getInvestFlowMixpanelCustomProperties } from '@utils/invest';
import { Link } from 'types/layout';
import { MixpanelContext } from 'types/analytics';
import { useAppStore } from '@stores/app';
import { useInvestmentTriggerStore } from '@stores/investment-trigger';
import { useInvestStore } from '@stores/invest';

interface Props {
	backLink?: Link;
	cancelLink?: Link;
	classes?: string;
	cancelClickText?: string;
	enableHomeLink?: boolean;
	theme?: string;
	setBorder?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
	backLink: undefined,
	cancelLink: undefined,
	cancelClickText: undefined,
	classes: '',
	enableHomeLink: true,
	theme: 'light',
	setBorder: true
});

const appStore = useAppStore();
const investStore = useInvestStore();
const investmentTriggerStore = useInvestmentTriggerStore();
const router = useRouter();
const route = useRoute();

const offeringId = computed((): string => investStore.investMetaData?.offering?.id ?? '');

const cohortId = computed((): string => (investStore.selectedWindow?.cohortId as string) ?? '');

const flowType = computed((): AccountInvestFlowType => {
	return route.params.flowType as AccountInvestFlowType;
});

const mixpanelCustomProperties = computed((): MixpanelContext => {
	return {
		...getInvestFlowMixpanelCustomProperties(flowType.value, offeringId.value, cohortId.value)
	};
});

const logoImgUrl = computed((): string => {
	return props.theme === 'dark'
		? `${appCloudfrontPath}/interface/logo-hz-white.svg`
		: `${appCloudfrontPath}/interface/logo-hz-color.svg`;
});

const logoRoute = computed((): string => {
	return appStore.isAuthenticated && appStore.isVerified ? 'account-overview' : 'home';
});

const logoClasses = computed((): Record<string, boolean> => {
	return {
		'header-unified__logo': true,
		'header-unified__logo--mobile': appStore.isMobile
	};
});

async function exitFlow(): Promise<void> {
	investmentTriggerStore.investmentTrigger = null;
	await followLink(props.cancelLink);
}

async function followLink(linkToFollow: Link | undefined): Promise<void> {
	try {
		await router.push({
			name: linkToFollow?.router,
			params: linkToFollow?.params,
			query: linkToFollow?.query
		});
	} catch {}
}

async function goToPreviousStep(): Promise<void> {
	await followLink(props.backLink);
}
</script>

<style scoped lang="scss">
.header {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.header-logo {
	position: absolute;
	left: 50%;
	transform: translateX(-50%);
}

.header-unified__logo {
	width: 137px;
	&--mobile {
		width: 83px;
		height: 16px;
	}
}
</style>
